import { sumMoney } from '@mms/shared';
import { useEffect, useRef, type MutableRefObject } from 'react';
import { Button, Form } from 'react-bootstrap';
import { z } from 'zod';
import type { Row } from '../api/types';
import type { AnyForm } from '../forms/fields';
import { amountReq, dateTimeReq, selectReq, toId, toText } from '../forms/zod';
import { useBalance, type BalanceKind } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import { FieldRenderer, type CrudConfig, type FieldDef } from './CrudScreen';
import { base, dateCol, noCol } from './transactions';

/**
 * Fund Transfer (requested 2026-10-16, migration 026): replaces the Dispatcher Transaction and Dispatcher Payment screens. Money moves FROM one account TO
 * another in one currency; each side is Cash, a Bank, a Seller, a Dispatcher or an Agent. The From side's balance goes down by the amount, the To side's up.
 * Remarks are optional (an automatic text is stored when empty).
 */
const TYPES = [
  { value: 'cash', label: 'Cash', kind: 'currencies' as BalanceKind },
  { value: 'bank', label: 'Bank', kind: 'banks' as BalanceKind },
  { value: 'seller', label: 'Seller', kind: 'purchasers' as BalanceKind },
  { value: 'dispatcher', label: 'Dispatcher', kind: 'suppliers' as BalanceKind },
  { value: 'agent', label: 'Agent', kind: 'agents' as BalanceKind },
];
const typeLabel = (t: string) => TYPES.find((x) => x.value === t)?.label ?? t;

const transferSchema = z
  .object({
    currencyId: selectReq('Select a currency'),
    fromType: selectReq('Select a type'), fromId: z.string(),
    toType: selectReq('Select a type'), toId: z.string(),
    amount: amountReq,
    transactionDate: dateTimeReq,
    remarks: z.string(),
  })
  .superRefine((v, ctx) => {
    for (const side of ['from', 'to'] as const) {
      if (v[`${side}Type`] && v[`${side}Type`] !== 'cash' && !v[`${side}Id`]) ctx.addIssue({ code: 'custom', path: [`${side}Id`], message: 'Select a party' });
    }
    if (v.fromType && v.fromType === v.toType && v.fromId && v.fromId === v.toId) ctx.addIssue({ code: 'custom', path: ['toId'], message: 'Must differ from the From account' });
  });

/** A seller / dispatcher lookup row holds a currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

/**
 * One side (From / To) of the form: the parties its type offers in the chosen currency, and the clean-up that keeps the party valid. The ids of
 * different types overlap (agent 1, seller 1, bank 1), so a new type always clears the party; Cash has no party of its own (it is held per currency,
 * so its "party" is the currency itself).
 */
function useSide(form: AnyForm, side: 'from' | 'to', currencyId: string) {
  const idName = `${side}Id`;
  const type = String(form.watch(`${side}Type`) ?? '');
  const partyId = String(form.watch(idName) ?? '');
  const agents = useLookup('agents');
  const sellers = useLookup('purchasers');
  const dispatchers = useLookup('suppliers');
  const banks = useLookup('banks');

  const rows: Row[] = !currencyId || !type ? []
    : type === 'cash' ? [{ id: currencyId, name: 'Cash' }]
    : type === 'bank' ? (banks.data ?? []).filter((b) => String(b.currencyId) === currencyId)
    : type === 'agent' ? (agents.data ?? []).filter((a) => holds(a, currencyId))
    : type === 'seller' ? (sellers.data ?? []).filter((p) => holds(p, currencyId))
    : (dispatchers.data ?? []).filter((p) => holds(p, currencyId));
  const listLoading = (type === 'bank' && banks.isPending) || (type === 'agent' && agents.isPending) || (type === 'seller' && sellers.isPending) || (type === 'dispatcher' && dispatchers.isPending);

  const lastType = useRef(type);
  useEffect(() => {
    if (lastType.current !== type) { lastType.current = type; if (type !== 'cash' && partyId) form.setValue(idName, ''); }
    if (type === 'cash') { if (partyId !== currencyId) form.setValue(idName, currencyId); return; }
    // a party that no longer fits the currency is dropped, but only once its list has loaded (an edit opens before it does)
    if (!listLoading && partyId && lastType.current === type && !rows.some((p) => String(p.id) === partyId)) form.setValue(idName, '');
  }, [type, currencyId, partyId, listLoading, agents.data, sellers.data, dispatchers.data, banks.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const party = rows.find((p) => String(p.id) === partyId);
  // a Dispatcher's Low balance alert for this currency (Dispatcher master)
  const alertLevel = type === 'dispatcher'
    ? Number(((party?.currencies as Row[] | undefined) ?? []).find((c) => String(c.currencyId) === currencyId)?.lowBalanceAlert ?? 0) : 0;
  const kind = TYPES.find((t) => t.value === type)?.kind;
  // the balance this side has now (Cash: the currency's cash; the party's own currency row for a Seller / Dispatcher)
  const balance = useBalance(kind ?? 'agents', kind && party ? partyId : undefined, currencyId);
  return { type, partyId, rows, party, alertLevel, lastType: lastType as MutableRefObject<string>, balance: kind && party ? balance.data : undefined };
}

export const fundTransfersConfig: CrudConfig = {
  ...base,
  modalSize: 'xl',
  title: 'Fund Transfer', singular: 'fund transfer', icon: 'arrow-left-right', menu: 'fund-transfer', endpoint: '/transactions/fund-transfer',
  saveAndNew: true,
  schema: transferSchema,
  defaults: () => ({ currencyId: '', fromType: '', fromId: '', toType: '', toId: '', amount: '', transactionDate: nowInput(), remarks: '' }),
  toForm: (r) => ({
    currencyId: String(r.currencyId), fromType: String(r.fromType), fromId: String(r.fromId), toType: String(r.toType), toId: String(r.toId),
    amount: r.amount, transactionDate: toDateTimeInput(r.transactionDate), remarks: r.remarks ?? '',
  }),
  toPayload: (v) => ({
    fromType: v.fromType, fromId: v.fromType === 'cash' ? null : toId(v.fromId), toType: v.toType, toId: v.toType === 'cash' ? null : toId(v.toId),
    currencyId: toId(v.currencyId), amount: v.amount.trim(), transactionDate: fromDateTimeInput(v.transactionDate), remarks: toText(v.remarks),
  }),
  rowLabel: (r) => `${r.transactionNo} (${r.fromName} → ${r.toName})`,
  useTable: () => {
    // #Ref, Date, From (with its type), To (with its type), Amount (with its currency), Remarks
    const side = (key: 'from' | 'to', header: string): Column => ({
      key: `${key}Name`, header, sortKey: key,
      render: (r) => <><span className="fw-semibold">{r[`${key}Name`]}</span><small className="text-body-secondary ms-1">{typeLabel(String(r[`${key}Type`]))}</small></>,
    });
    const columns: Column[] = [
      { ...noCol, header: '#Ref', hideOnMobile: false },
      dateCol,
      side('from', 'From'),
      side('to', 'To'),
      { key: 'amount', header: 'Amount', sortKey: 'amount', align: 'end', render: (r) => <Amount value={r.amount} code={r.currencyCode} /> },
      { key: 'remarks', header: 'Remarks', className: 'text-truncate', hideOnMobile: true, render: (r) => <span title={r.remarks ?? ''} className="d-inline-block text-truncate" style={{ maxWidth: 260 }}>{r.remarks ?? ''}</span> },
    ];
    const typeOptions = TYPES.map((t) => ({ value: t.value, label: t.label }));
    return { columns, filters: [{ param: 'fromType', label: 'From', options: typeOptions }, { param: 'toType', label: 'To', options: typeOptions }] };
  },
  useForm: (form, { readOnly }) => {
    const currencies = useLookup('currencies');
    const currencyId = String(form.watch('currencyId') ?? '');
    const amount = String(form.watch('amount') ?? '');
    const from = useSide(form, 'from', currencyId);
    const to = useSide(form, 'to', currencyId);
    const currencyOptions = toOptions(currencies.data, (c) => c.code as string);
    const typeOptions = TYPES.map((t) => ({ value: t.value, label: t.label }));

    const swap = () => {
      // the types and ids go over together, so neither side's "new type clears the party" rule may fire
      const f = { type: from.type, id: from.partyId };
      const t = { type: to.type, id: to.partyId };
      from.lastType.current = t.type;
      to.lastType.current = f.type;
      const set = (name: string, value: string) => form.setValue(name, value, { shouldDirty: true });
      set('fromType', t.type); set('fromId', t.id); set('toType', f.type); set('toId', f.id);
    };

    const field = (def: FieldDef, col = 12) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const hint = (s: ReturnType<typeof useSide>) => (!s.type ? 'Choose the type first' : !currencyId ? 'Choose the currency first' : 'Select…');
    const partyField = (side: 'from' | 'to', s: ReturnType<typeof useSide>): FieldDef => ({
      name: `${side}Id`, label: 'Party', type: 'select', required: true, options: toOptions(s.rows),
      placeholder: s.type === 'cash' ? undefined : hint(s), disabled: !s.type || !currencyId || s.type === 'cash',
    });

    // sending money OUT of a dispatcher that is already below its Low balance alert (or would fall below it) needs topping up first
    const fromBal = from.balance ? Number(from.balance.balance) : null;
    const fromAfter = fromBal !== null && Number(amount) > 0 ? fromBal - Number(amount) : fromBal;
    const lvl = from.alertLevel;
    const nameOf = String(from.party?.name ?? '');
    const code = from.balance?.currencyCode ?? '';
    const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const lowText = lvl > 0 && fromBal !== null && fromBal >= 0 && fromBal < lvl ? `${nameOf} holds ${code} ${money(fromBal)}, below its alert of ${money(lvl)}`
      : lvl > 0 && fromAfter !== null && fromAfter < lvl ? `After this transfer ${nameOf} would hold ${code} ${money(Math.max(fromAfter, 0))}, below its alert of ${money(lvl)}` : null;

    const card = (side: 'from' | 'to') => {
      const s = side === 'from' ? from : to;
      const isFrom = side === 'from';
      return (
        <section aria-label={isFrom ? 'From' : 'To'} className="rounded border p-3 h-100" style={{ borderLeft: `3px solid var(--bs-${isFrom ? 'warning' : 'success'})` }}>
          <div className={`fw-semibold mb-2 text-${isFrom ? 'warning' : 'success'}`}>
            <i className={`bi bi-arrow-${isFrom ? 'up-right' : 'down-left'} me-1`} aria-hidden />{isFrom ? 'From' : 'To'}
          </div>
          <div className="row">
            <div className="col-6">
              {isFrom
                ? field({ name: 'currencyId', label: 'Currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })
                : (
                  // both sides are in the same currency, so this one follows the From side
                  <Form.Group className="mb-3" controlId="toCurrency">
                    <Form.Label>Currency <span className="text-danger">*</span></Form.Label>
                    <Form.Select disabled value={currencyId} title="Both sides use the same currency">
                      <option value="">Select…</option>
                      {currencyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                )}
            </div>
            <div className="col-6">{field({ name: `${side}Type`, label: 'Type', type: 'select', required: true, options: typeOptions, placeholder: 'Select…' })}</div>
          </div>
          <div className="row">{field(partyField(side, s))}</div>
          {isFrom && lowText && <div className="small text-warning" role="status" data-testid="from-low-balance"><i className="bi bi-exclamation-triangle me-1" aria-hidden />{lowText}</div>}
        </section>
      );
    };

    const body = (
      <>
        <div className="row g-2 align-items-center mb-3">
          <div className="col-12 col-md">{card('from')}</div>
          <div className="col-12 col-md-auto text-center">
            <Button type="button" variant="outline-secondary" onClick={swap} disabled={readOnly} aria-label="Swap From and To" title="Swap From and To">
              <i className="bi bi-arrow-left-right" aria-hidden />
            </Button>
          </div>
          <div className="col-12 col-md">{card('to')}</div>
        </div>
        <div className="row">{field({ name: 'amount', label: 'Amount', type: 'money', required: true, placeholder: '0.00' })}</div>
        <div className="row">
          <div className="col-md-6">{field({ name: 'transactionDate', label: 'Date & time', type: 'datetime', required: true })}</div>
          <div className="col-md-6">{field({ name: 'remarks', label: 'Remarks (optional)', type: 'text', placeholder: 'Auto text is used if left empty' })}</div>
        </div>
      </>
    );

    // what saving does: the From side goes down by the amount and the To side goes up by it
    const line = (name: string | undefined, s: ReturnType<typeof useSide>, sign: '-' | '') => {
      if (!s.balance || !name || !amount) return null;
      const after = sumMoney([s.balance.balance, `${sign}${amount}`], 3);
      return (
        <div>
          {name} <Amount value={s.balance.balance} decimals={3} code={s.balance.currencyCode} />
          <i className="bi bi-arrow-right mx-1" aria-hidden />
          <strong className={sign ? 'text-warning' : 'text-success'}><Amount value={after} decimals={3} code={s.balance.currencyCode} /></strong>
        </div>
      );
    };
    const a = line(from.party?.name as string | undefined, from, '-');
    const b = line(to.party?.name as string | undefined, to, '');
    const summary = a || b ? <span data-testid="transfer-effect">{a}{b}</span> : null;

    return { body, summary, fields: [] };
  },
};
